import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BookOpen, LogOut, Menu, X, CreditCard, UserPlus, Heart, Shield, Key, Package, Calendar, GraduationCap } from "lucide-react";
import { useState, useMemo } from "react";
import BackgroundAnimations from "@/components/BackgroundAnimations";

const adminLinks = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/courses", label: "Courses", icon: BookOpen },
  { to: "/admin/users", label: "Users & Roles", icon: Shield },
  { to: "/admin/roles", label: "Permissions", icon: Key },
  { to: "/admin/sessions", label: "Sessions", icon: Calendar },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/enroll", label: "Enrol Users", icon: UserPlus },
  { to: "/admin/inventory", label: "Inventory", icon: Package },
  { to: "/admin/feed", label: "Studio Feed", icon: Heart },
];

const instructorLinks = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/courses", label: "Courses", icon: BookOpen },
  { to: "/admin/students", label: "Students", icon: GraduationCap },
  { to: "/admin/enroll", label: "Enrol Students", icon: UserPlus },
  { to: "/admin/sessions", label: "Sessions", icon: Calendar },
  { to: "/admin/feed", label: "Studio Feed", icon: Heart },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = useMemo(() => {
    if (user?.role === "instructor") return instructorLinks;
    return adminLinks;
  }, [user?.role]);

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-3 mb-10">
        <img src="/kalemash-logo.png" alt="Kalemash" className="w-9 h-9 rounded-xl" />
        <span className="font-heading text-2xl text-[#5C3A2E] font-semibold">Kalemash</span>
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-4 font-body">Studio Manager</p>
      <nav className="flex-1 space-y-1">
        {navLinks.map((link) => {
          const Icon = link.icon;
          const active = isActive(link.to);
          return (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active
                  ? "bg-[#5C3A2E] text-white"
                  : "text-[#2B2B2B]/70 hover:bg-[#EADBC8]/40 hover:text-[#5C3A2E]"
              }`}
            >
              <Icon className="w-4.5 h-4.5" />
              {link.label}
            </Link>
          );
        })}
      </nav>
      <Button
        data-testid="admin-logout-button"
        variant="ghost"
        onClick={logout}
        className="w-full justify-start text-[#2B2B2B]/60 hover:text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-xl mt-4"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign out
      </Button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F7F3EF] flex relative overflow-hidden">
      <BackgroundAnimations />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white/90 backdrop-blur-sm border-r border-[#EADBC8]/50 p-6 flex-col fixed h-full z-10">
        <SidebarContent />
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-b border-[#EADBC8]/50 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/kalemash-logo.png" alt="Kalemash" className="w-8 h-8 rounded-lg" />
          <span className="font-heading text-xl text-[#5C3A2E] font-semibold">Kalemash</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setMobileOpen(!mobileOpen)} className="text-[#5C3A2E]">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 animate-fade-in">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white p-6 flex flex-col shadow-xl animate-slide-in-left overflow-y-auto">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 relative z-[1]">
        <div className="p-6 sm:p-8 max-w-6xl">
          {children}
        </div>
      </main>
    </div>
  );
}
